import type { RecurringLens } from '../../context/recurringLensContextCore'
import type { TimelineElement } from '../../context/TimelineContext'
import { isChecklistHelperVisible } from '../../services/checklistMutationService'
import type { ChecklistItem as BoardChecklistItem } from '../../types/schema'
import { isGateNode, normalizeBoardItem, normalizeWeightage } from './boardItem'

export const getConnectedChecklistNodeIds = (
  nodes: readonly {
    id: string
    prerequisites?: readonly string[] | null
    dependencyNodeIds?: readonly unknown[] | null
    blockedByNodeIds?: readonly unknown[] | null
    isBlockerForNodeIds?: readonly unknown[] | null
    gateInputIds?: readonly string[] | null
    blockedByExternalId?: string | null
    interWorkspaceTargetId?: string | null
  }[],
): Set<string> => {
  const connectedIds = new Set<string>()
  for (const node of nodes) {
    const hasPrereqs = Array.isArray(node.prerequisites) && node.prerequisites.length > 0
    const hasDepNodeIds = Array.isArray(node.dependencyNodeIds) && node.dependencyNodeIds.length > 0
    const hasBlockedBy = Array.isArray(node.blockedByNodeIds) && node.blockedByNodeIds.length > 0
    const hasBlockerFor = Array.isArray(node.isBlockerForNodeIds) && node.isBlockerForNodeIds.length > 0
    const hasGateInputs = Array.isArray(node.gateInputIds) && node.gateInputIds.length > 0
    const hasExternal = Boolean(node.blockedByExternalId || node.interWorkspaceTargetId)

    if (hasPrereqs || hasDepNodeIds || hasBlockedBy || hasBlockerFor || hasGateInputs || hasExternal) {
      connectedIds.add(node.id)
    }

    if (Array.isArray(node.prerequisites)) {
      node.prerequisites.forEach((id) => typeof id === 'string' && connectedIds.add(id))
    }
    if (Array.isArray(node.dependencyNodeIds)) {
      node.dependencyNodeIds.forEach((item) => {
        if (typeof item === 'string') connectedIds.add(item)
        else if (item && typeof item === 'object' && typeof (item as { id?: string }).id === 'string') {
          connectedIds.add((item as { id: string }).id)
        }
      })
    }
    if (Array.isArray(node.blockedByNodeIds)) {
      node.blockedByNodeIds.forEach((item) => {
        if (item && typeof item === 'object' && typeof (item as { id?: string }).id === 'string') {
          connectedIds.add((item as { id: string }).id)
        }
      })
    }
    if (Array.isArray(node.isBlockerForNodeIds)) {
      node.isBlockerForNodeIds.forEach((item) => {
        if (item && typeof item === 'object' && typeof (item as { id?: string }).id === 'string') {
          connectedIds.add((item as { id: string }).id)
        }
      })
    }
    if (Array.isArray(node.gateInputIds)) {
      node.gateInputIds.forEach((id) => typeof id === 'string' && connectedIds.add(id))
    }
  }
  return connectedIds
}

export const overlayLayoutPositionsForWorkspace = async (
  workspaceId: string,
  recurringLens: RecurringLens | null | undefined,
  boardNodes: BoardChecklistItem[],
): Promise<BoardChecklistItem[]> => {
  try {
    const { bookIdFor } = await import('../../types/checklistBooks')
    const { getPage, pageKeyFor } = await import('../../services/checklistBookService')
    const { getLayoutVersion } = await import('../../services/layoutVersionService')
    const { fetchWorkspaceRawTimezone } = await import('../../services/checklistStatusWriter')
    const timeZone = await fetchWorkspaceRawTimezone(workspaceId).catch(() => undefined)
    const types: Array<'INDIVIDUAL' | 'DAILY' | 'WEEKLY'> = ['INDIVIDUAL', 'DAILY', 'WEEKLY']
    const today = new Date()
    const positionByNode = new Map<string, { x: number; y: number }>()
    await Promise.all(types.map(async (type) => {
      const bookId = bookIdFor(workspaceId, type)
      const pageKey = recurringLens && type === recurringLens.mode
        ? recurringLens.focusedKey
        : pageKeyFor(type, today, timeZone)
      const page = await getPage(bookId, pageKey).catch(() => null)
      const layoutVersionId = page?.layoutVersionId
      if (!layoutVersionId) return
      const layout = await getLayoutVersion(bookId, layoutVersionId).catch(() => null)
      if (!layout) return
      Object.values(layout.nodes).forEach((entry) => {
        const pos = entry.position
        if (typeof pos?.x === 'number' && typeof pos?.y === 'number') {
          positionByNode.set(entry.checklistId, { x: pos.x, y: pos.y })
        }
      })
    }))
    if (positionByNode.size === 0) return boardNodes
    return boardNodes.map((n) => {
      const overlay = positionByNode.get(n.id)
      return overlay ? { ...n, position: overlay } : n
    })
  } catch {
    return boardNodes
  }
}

export const getSourceChecklistId = (elementId: string, node: BoardChecklistItem & { sourceChecklistId?: string | null }) => {
  if (typeof node.sourceChecklistId === 'string' && node.sourceChecklistId.trim()) return node.sourceChecklistId
  const prefix = `${elementId}_`
  return node.id.startsWith(prefix) ? node.id.slice(prefix.length) : node.id
}

export interface LiveChecklistReconciliationResult {
  updatedExisting: BoardChecklistItem[]
  hasOverlayChanges: boolean
  candidateRowsToAppend: BoardChecklistItem[]
  normalizedNewNodes: BoardChecklistItem[]
}

export const reconcileLiveChecklistBoardNodes = ({
  currentNodes,
  timelineRows,
  containerItemId,
  workspaceId,
  deletedSourceIds,
  isDashboardContainer,
  recurringLens,
  activeWorkspaceOwnerId,
  currentUserId,
}: {
  currentNodes: readonly (BoardChecklistItem & { sourceChecklistId?: string | null })[]
  timelineRows: readonly BoardChecklistItem[]
  containerItemId: string
  workspaceId: string
  deletedSourceIds: ReadonlySet<string>
  isDashboardContainer: (containerId: string) => boolean
  recurringLens: RecurringLens | null | undefined
  activeWorkspaceOwnerId?: string
  currentUserId?: string
}): LiveChecklistReconciliationResult => {
  const bySourceId = new Map<string, BoardChecklistItem>()
  for (const row of timelineRows) {
    if (row && typeof row.id === 'string') bySourceId.set(row.id, row)
  }

  let hasOverlayChanges = false
  const currentIds = new Set<string>()
  const currentSourceIds = new Set<string>()

  const updatedExisting = currentNodes.map((node) => {
    currentIds.add(node.id)
    const sourceId = getSourceChecklistId(containerItemId, node)
    if (sourceId) currentSourceIds.add(sourceId)
    if (node.sourceChecklistId) currentSourceIds.add(node.sourceChecklistId)

    const live = bySourceId.get(sourceId)
    if (!live) return node
    const merged: BoardChecklistItem = {
      ...node,
      ...live,
      id: node.id,
      position: node.position,
      prerequisites: node.prerequisites,
      dependencyNodeIds: node.dependencyNodeIds,
      invalidDependencyNodeIds: node.invalidDependencyNodeIds,
      blockedByExternal: node.blockedByExternal,
      blockedByExternalId: node.blockedByExternalId,
      interWorkspaceTargetId: node.interWorkspaceTargetId,
    }
    if (
      merged.assigneeId !== node.assigneeId
      || merged.assigneeName !== node.assigneeName
      || merged.title !== node.title
      || merged.boardStatus !== node.boardStatus
      || merged.priority !== node.priority
      || merged.startDate !== node.startDate
      || merged.endDate !== node.endDate
      || merged.isCompleted !== node.isCompleted
      || merged.weightage !== node.weightage
      || merged.description !== node.description
      || merged.completionCriteria !== node.completionCriteria
      || merged.isReady !== node.isReady
      || merged.proofReviewStatus !== node.proofReviewStatus
      || merged.recordVersion !== node.recordVersion
    ) {
      hasOverlayChanges = true
      return merged
    }
    return node
  })

  const candidateRowsToAppend = timelineRows.filter((candidate) => {
    if (!candidate || typeof candidate.id !== 'string') return false
    if (currentIds.has(candidate.id) || currentSourceIds.has(candidate.id)) return false
    if (deletedSourceIds.has(candidate.id)) return false
    if (candidate.workspaceId !== workspaceId) return false
    if ((candidate.board ?? 'DASHBOARD') !== 'DASHBOARD') return false
    if (!isDashboardContainer(String(candidate.parentContainerId ?? ''))) return false
    if (candidate.isArchived === true) return false
    if (recurringLens) {
      if (candidate.checklistType !== recurringLens.mode) return false
    } else if (candidate.checklistType && candidate.checklistType !== 'INDIVIDUAL') {
      return false
    }
    return true
  })

  const normalizedNewNodes = candidateRowsToAppend.map((row, index) => {
    const normalized = normalizeBoardItem(row.id, row, index)
    if (!normalized.createdById) {
      normalized.createdById = activeWorkspaceOwnerId ?? currentUserId ?? ''
      normalized.createdByName = 'Workspace admin'
    }
    return normalized
  })

  return {
    updatedExisting,
    hasOverlayChanges,
    candidateRowsToAppend,
    normalizedNewNodes,
  }
}

export const nodesToElementChecklist = (elementId: string, boardNodes: BoardChecklistItem[]) =>
  boardNodes
    .filter((node) => node.parentContainerId === elementId)
    // Gates (AND/OR logic gates) never get mirrored into the element checklist
    // — they're pure flow-control nodes, not checklist items. Mirroring them
    // here used to cause a feedback loop: the mirror write triggered a fresh
    // `item.checklist` from Firestore, which seeded "AND gate" titled regular
    // checklist nodes back into local state, overwriting the real gate.
    .filter((node) => !isGateNode(node))
    .filter((node) => node.title.trim())
    .sort((a, b) => a.position.y - b.position.y || a.position.x - b.position.x)
    .map((node) => ({
      id: getSourceChecklistId(elementId, node),
      text: node.title.trim(),
      isCompleted: Boolean(node.isCompleted),
      isHelperVisible: isChecklistHelperVisible(node.isHelperVisible),
      weightage: normalizeWeightage(node.weightage),
    }))

export const checklistMirrorsMatch = (
  currentChecklist: TimelineElement['checklist'],
  nextChecklist: TimelineElement['checklist'],
) => {
  if (currentChecklist.length !== nextChecklist.length) return false
  return currentChecklist.every((item, index) => {
    const next = nextChecklist[index]
    return item.id === next.id
      && item.text === next.text
      && Boolean(item.isCompleted) === Boolean(next.isCompleted)
      && normalizeWeightage(item.weightage) === normalizeWeightage(next.weightage)
  })
}

export const isRecurringChecklistType = (type: BoardChecklistItem['checklistType'] | null | undefined) =>
  type === 'DAILY' || type === 'WEEKLY'
